import {
  featuredPlatforms,
  majorInitiatives,
  otherInitiatives,
} from "@/mocks/platforms";

export default function Platforms() {
  return (
    <section
      id="platforms"
      className="w-full bg-background-50 py-10 md:py-12 lg:py-14"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary-600 mb-4">
            Ecosystem
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
            Platforms &amp; Initiatives
          </h2>
        </div>

        {/* Featured Platforms */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-8 md:mb-10">
          {featuredPlatforms.map((platform) => {
            const body = (
              <>
                {platform.logo ? (
                  <div className="w-full h-12 flex items-center justify-start mb-4">
                    <img
                      src={platform.logo}
                      alt={platform.name}
                      className="max-h-full max-w-[140px] object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 mb-4">
                    <i className={`${platform.icon} text-lg`} />
                  </div>
                )}
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2">
                  {platform.name}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {platform.description}
                </p>
                {platform.cta && (
                  <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 whitespace-nowrap">
                    {platform.cta}
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                )}
                {platform.owned && (
                  <span className="mt-auto pt-4 inline-flex items-center w-fit px-2.5 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                    Owned by A&amp;J
                  </span>
                )}
              </>
            );

            if (platform.link) {
              return (
                <a
                  key={platform.name}
                  href={platform.link}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex flex-col bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-7 hover:border-primary-300/60 transition-colors"
                >
                  {body}
                </a>
              );
            }

            return (
              <div
                key={platform.name}
                className="flex flex-col bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-7"
              >
                {body}
              </div>
            );
          })}
        </div>

        {/* Major Initiatives */}
        <div className="mb-8 md:mb-10">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
              Major Initiatives
            </h3>
            <span className="h-px flex-1 bg-background-200" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {majorInitiatives.map((initiative) => (
              <a
                key={initiative.name}
                href={initiative.link}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="group flex flex-col bg-accent-100/60 border border-accent-200/70 rounded-lg p-6 md:p-8 hover:border-accent-300/70 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  {initiative.logo ? (
                    <div className="w-12 h-12 flex items-center justify-center rounded-md bg-white overflow-hidden">
                      <img
                        src={initiative.logo}
                        alt={initiative.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                      <i className={`${initiative.icon} text-xl`} />
                    </div>
                  )}
                  {initiative.category && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-background-100 text-foreground-700 rounded-full">
                      {initiative.category}
                    </span>
                  )}
                </div>

                <h4 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2">
                  {initiative.name}
                </h4>

                {initiative.tag && (
                  <p className="text-xs font-semibold tracking-wide text-accent-700 mb-2">
                    {initiative.tag}
                  </p>
                )}

                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  {initiative.description}
                </p>

                <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 whitespace-nowrap">
                  {initiative.cta}
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Other Initiatives */}
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8">
          <h3 className="text-sm font-semibold text-foreground-800 uppercase tracking-wide mb-4">
            Other Initiatives
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherInitiatives.map((initiative) => {
              const chipClass =
                "px-3 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-900 rounded-md";

              if (initiative.link) {
                return (
                  <a
                    key={initiative.name}
                    href={initiative.link}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={`${chipClass} hover:bg-secondary-200 transition-colors whitespace-nowrap`}
                  >
                    {initiative.name}
                  </a>
                );
              }

              return (
                <span key={initiative.name} className={chipClass}>
                  {initiative.name}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}